import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { toNano, beginCell, Address } from '@ton/core';
import { Buffer } from 'buffer';
import PiggyBank from '../components/PiggyBank';
import BurnWidget from '../components/BurnWidget';
import api, { gameAPI } from '../services/api';
import { formatNumber, formatPercent, formatPercentValue } from '../utils/formatters';
import SoundManager from '../services/SoundManager';
import spermLogo from '../assets/sperm-logo.png';
import tonmoonLogo from '../assets/tonmoon-logo.png';
import './StakingPage.css';
import type { PaymentMethod, BurnSource } from '../components/MarathonModal';

// Lazy load модальных окон для ускорения первой загрузки
const StakingModal = lazy(() => import('../components/StakingModal'));
const StakingSuccessModal = lazy(() => import('../components/StakingSuccessModal'));
const MarathonModal = lazy(() => import('../components/MarathonModal'));
const MarathonContributeModal = lazy(() => import('../components/MarathonContributeModal'));
const StarWithdrawModal = lazy(() => import('../components/StarWithdrawModal'));
const StakingInfoModal = lazy(() => import('../components/StakingInfoModal'));
const PromoSuccessModal = lazy(() => import('../components/PromoSuccessModal'));
const MarathonCreatedModal = lazy(() => import('../components/MarathonCreatedModal'));

// Полифилл для Buffer в браузере
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer;
}

// Функция для правильного склонения слова "день"
const getDaysDeclension = (days: number): string => {
  const absNum = Math.abs(days);
  const lastTwo = absNum % 100;
  const lastOne = absNum % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return 'дней';
  }
  if (lastOne === 1) {
    return 'день';
  }
  if (lastOne >= 2 && lastOne <= 4) {
    return 'дня';
  }
  return 'дней';
};

// Функция для форматирования дохода (показывать больше знаков для малых сумм)
const formatDailyIncome = (income: number): string => {
  if (income === 0) return '0';
  if (income < 0.01) return income.toFixed(4);
  if (income < 0.1) return income.toFixed(3);
  if (income < 100) return income.toFixed(2);  // Для сумм до 100 - 2 знака после запятой
  return income.toFixed(1);  // Для больших сумм - 1 знак
};

const StakingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, updateBalance, spermPrice, tonPrice } = useStore(); // Получаем данные пользователя и курсы из стора
  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();

  // Интернациональная функция для отображения дней
  const getDaysText = (days: number): string => {
    if (i18n.language === 'ru') {
      return getDaysDeclension(days);
    }
    return days === 1 ? 'day' : 'days';
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStaking, setSelectedStaking] = useState<'lunar' | 'star' | null>(null);
  const [marathonModalOpen, setMarathonModalOpen] = useState(false);
  const [marathonAmount, setMarathonAmount] = useState(''); // 💵 Сумма для марафона в USDT
  const [marathonLoading, setMarathonLoading] = useState(false);
  const [starWithdrawModalOpen, setStarWithdrawModalOpen] = useState(false);
  const [selectedDepositForWithdraw, setSelectedDepositForWithdraw] = useState<any>(null);
  const [marathonContributeModalOpen, setMarathonContributeModalOpen] = useState(false);
  const [selectedMarathonForContribute, setSelectedMarathonForContribute] = useState<any>(null);

  // 🛡️ Защита от дабл-клика по кнопкам марафона (claim/withdraw).
  // Ref — синхронный замок (срабатывает до ре-рендера, ловит частые тапы),
  // state — только для disabled/индикации в UI. Ключ — id марафона.
  const marathonBusyRef = useRef<Set<number>>(new Set());
  const [marathonBusy, setMarathonBusy] = useState<Record<number, boolean>>({});
  // 🎟️ Активация промокода (акция Lunar Start)
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  // Праздничная модалка после активации
  const [promoSuccessOpen, setPromoSuccessOpen] = useState(false);
  const [promoSuccessAmount, setPromoSuccessAmount] = useState(50);
  const [promoSuccessCode, setPromoSuccessCode] = useState('');
  // Праздничная модалка создания марафона
  const [marathonCreatedOpen, setMarathonCreatedOpen] = useState(false);
  const [marathonCreatedAmount, setMarathonCreatedAmount] = useState(0);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successData, setSuccessData] = useState({
    stakingAmount: 0,
    claimedAmount: 0,
    newBalance: 0,
    stakingType: 'lunar' as 'lunar' | 'star'
  });
  const [userBalance, setUserBalance] = useState(0);
  const [usdtBalance, setUsdtBalance] = useState(0);
  const [stakingSettings, setStakingSettings] = useState({
    lunar_enabled: true,
    star_enabled: false,
    lunar_apr: 1.0, // 1% в день
    star_apr: 0.5, // 0.5% в день
    lunar_daily_growth: 0.01, // Рост APR в день (+0.01%)
    star_daily_growth: 0.02,
    lunar_claim_interval: 24, // Часов между claim
    lunar_lock_period: 365, // Дней блокировки
    star_claim_interval: 0, // Без ограничений
    star_withdrawal_penalty: 10, // Процент комиссии
    lunar_min: 100,
    lunar_max: 1000000,
    star_min: 100,
    star_max: 500000
  });
  const [userDeposits, setUserDeposits] = useState<any[]>([]);
  const [poolExhausted, setPoolExhausted] = useState(false);
  const [activeTab, setActiveTab] = useState<'lunar' | 'star' | 'marathon'>('marathon');
  const [expandedDeposit, setExpandedDeposit] = useState<number | null>(null);
  const [showInfoModal, setShowInfoModal] = useState<'lunar' | 'star' | 'marathon' | null>(null);
  const [stakingStats, setStakingStats] = useState({
    totalStaked: 0,
    totalRealStaked: 0, // Реальная сумма депозита (amount)
    totalEarned: 0,
    activeDeposits: 0,
    dailyIncome: 0,
    lunarDailyIncome: 0, // Доход только от lunar стейкингов
    starDailyIncome: 0,  // Доход только от star стейкингов
    // USDT статистика для Лунного Марафона
    totalStakedUsdt: 0,
    totalEarnedUsdt: 0,
    dailyIncomeUsdt: 0,
    marathonDeposits: 0
  });

  useEffect(() => {
    // Устанавливаем баланс из стора сразу
    if (user?.balance) {
      setUserBalance(user.balance);
    }
    // Параллельная загрузка всех данных для ускорения
    Promise.all([
      fetchUserData(),
      fetchStakingSettings(),
      fetchUserDeposits()
    ]).catch(console.error);
  }, [user]);

  const fetchUserData = async () => {
    try {
      const response = await fetch('/api/user/profile', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // API возвращает data напрямую, не data.user
        const balance = data.data?.balance || data.balance || 0;
        setUserBalance(parseFloat(balance) || 0);
        // Загружаем USDT баланс
        const usdtBal = data.data?.usdt_balance || data.usdt_balance || 0;
        setUsdtBalance(parseFloat(usdtBal) || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных пользователя:', error);
    }
  };

  const fetchStakingSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/staking/settings');

      if (response.data.success) {
        const settings = response.data;
        setPoolExhausted(settings.staking_pool_exhausted === true);
        setStakingSettings({
          lunar_enabled: settings.lunar_enabled !== false,
          star_enabled: settings.star_enabled !== false,
          lunar_apr: settings.lunar_base_apr || 1.0, // 1% в день по умолчанию
          star_apr: settings.star_base_apr || 0.5, // 0.5% в день по умолчанию
          lunar_daily_growth: settings.lunar_daily_growth || 0.01,
          star_daily_growth: settings.star_daily_growth || 0.02,
          lunar_claim_interval: settings.lunar_claim_interval || 24,
          lunar_lock_period: settings.lunar_lock_period || 365,
          star_claim_interval: settings.star_claim_interval || 0,
          star_withdrawal_penalty: settings.star_withdrawal_penalty || 10,
          lunar_min: settings.lunar_min_amount || 100,
          lunar_max: settings.lunar_max_amount || 1000000,
          star_min: settings.star_min_amount || 100,
          star_max: settings.star_max_amount || 500000
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек стейкинга:', error);
      // Используем дефолтные значения если API недоступен
    }
  };

  const fetchUserDeposits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/staking/my');
      
      if (response.data.success) {
        const deposits = response.data.data || [];
        // Маппим поля из API к ожидаемому формату
        const mappedDeposits = deposits.map((dep: any) => ({
          ...dep,
          type: dep.staking_type || dep.type, // staking_type -> type
          amount: parseFloat(dep.amount) || 0,
          current_apr: parseFloat(dep.current_apr) || 1.0,
          total_earned: parseFloat(dep.total_earned) || 0,
          created_at: dep.created_at
        }));
        setUserDeposits(mappedDeposits);
        
        // Рассчитываем статистику раздельно для SPERM и USDT
        let totalStaked = 0;      // SPERM visual_balance (lunar + star)
        let totalRealStaked = 0;  // SPERM реальный депозит (amount)
        let totalEarned = 0;      // SPERM
        let dailyIncome = 0;      // SPERM (суммарный)
        let lunarDailyIncome = 0; // Доход только от lunar
        let starDailyIncome = 0;  // Доход только от star
        let activeCount = 0;

        // USDT статистика (marathon)
        let totalStakedUsdt = 0;
        let totalEarnedUsdt = 0;
        let dailyIncomeUsdt = 0;
        let marathonCount = 0;

        mappedDeposits.forEach((deposit: any) => {
          // Показываем active и paused (замороженные марафоны)
          if (deposit.status === 'active' || deposit.status === 'paused') {
            const depositType = deposit.type || deposit.staking_type;
            const isLunar = depositType === 'lunar';
            const isMarathon = depositType === 'marathon';
            const currency = deposit.currency || 'SPERM';

            // ✅ ИСПРАВЛЕНО: Для отображения используем visual_balance, для дохода - income_base_amount
            const displayAmount = isLunar
              ? parseFloat(deposit.visual_balance || deposit.amount)
              : parseFloat(deposit.actual_amount || deposit.amount);

            // ✅ Для расчёта ДОХОДА используем income_base_amount (реальный amount)
            const incomeBaseAmount = deposit.income_base_amount
              ? parseFloat(deposit.income_base_amount)
              : parseFloat(deposit.amount); // Fallback на amount

            if (isMarathon || currency === 'USDT') {
              // USDT статистика для марафона
              totalStakedUsdt += displayAmount;
              totalEarnedUsdt += deposit.total_earned || 0;
              // Марафон: используем таблицу выплат по периодам (% от накопленного за 14 дней)
              // Таблица выплат из GitBook (% от накопленного за 14 дней)
              const MARATHON_PAYOUT_RATES: Record<number, number> = {
                1: 30.0, 2: 27.9, 3: 26.0, 4: 24.3, 5: 22.7,
                6: 21.3, 7: 20.0, 8: 19.1, 9: 17.9, 10: 16.9,
                11: 15.9, 12: 15.1, 13: 14.3, 14: 13.5, 15: 12.9,
                16: 12.2, 17: 11.6, 18: 11.1, 19: 10.6, 20: 10.2
              };
              // Период берём из бэкенда (subscription_period)
              const backendPeriod = deposit.subscription_period || 1;
              // Если марафон заморожен — показываем доход предыдущего периода
              const currentPeriod = deposit.is_frozen && backendPeriod > 1 ? backendPeriod - 1 : backendPeriod;
              const payoutPercent = MARATHON_PAYOUT_RATES[currentPeriod] || 9.7;
              // Дневной доход = amount * payoutPercent% / 14 дней
              const marathonDailyIncome = (incomeBaseAmount * payoutPercent) / 100 / 14;
              dailyIncomeUsdt += marathonDailyIncome;
              marathonCount++;
            } else {
              // SPERM статистика для lunar и star
              totalStaked += displayAmount;
              // Реальный депозит (amount без x2)
              totalRealStaked += parseFloat(deposit.amount) || 0;
              totalEarned += deposit.total_earned || 0;

              // ✅ ИСПРАВЛЕНО: Для lunar доход от visual_balance (eternal regression)
              // displayAmount для lunar = visual_balance, для star = actual_amount
              const depositDailyIncome = isLunar
                ? (displayAmount * (deposit.effective_apr || deposit.current_apr || 1)) / 100  // lunar: от visual
                : (incomeBaseAmount * (deposit.effective_apr || deposit.current_apr || 1)) / 100; // star: от amount

              dailyIncome += depositDailyIncome;
              // Раздельный учёт для детализации
              if (isLunar) {
                lunarDailyIncome += depositDailyIncome;
              } else {
                starDailyIncome += depositDailyIncome;
              }
            }

            activeCount++;
          }
        });

        setStakingStats({
          totalStaked,
          totalRealStaked,
          totalEarned,
          activeDeposits: activeCount,
          dailyIncome,
          lunarDailyIncome,
          starDailyIncome,
          totalStakedUsdt,
          totalEarnedUsdt,
          dailyIncomeUsdt,
          marathonDeposits: marathonCount
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки депозитов:', error);
    }
  };

  const handleStakingClick = (type: 'lunar' | 'star') => {
    if (poolExhausted) {
      alert(t('staking.poolExhaustedAlert'));
      return;
    }
    if (type === 'lunar' && !stakingSettings.lunar_enabled) {
      alert(t('staking.lunarUnavailable'));
      return;
    }
    if (type === 'star' && !stakingSettings.star_enabled) {
      alert(t('staking.starUnavailable'));
      return;
    }

    setSelectedStaking(type);
    setModalOpen(true);
  };

  // Обработчик стейкинга с выбором способа оплаты
  const handleConfirmStaking = async (amount: number, paymentMethod: 'balance' | 'piggybank' | 'tonkeeper') => {
    if (!selectedStaking) return;

    // Оплата с баланса SPERM или из копилки
    if (paymentMethod === 'balance' || paymentMethod === 'piggybank') {
      try {
        const response = await fetch('/api/staking/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            type: selectedStaking,
            amount,
            source: paymentMethod // 'balance' или 'piggybank'
          })
        });

        if (response.ok) {
          const data = await response.json();

          // Закрываем модальное окно создания
          setModalOpen(false);
          setSelectedStaking(null);

          // Подготавливаем данные для модального окна успеха
          setSuccessData({
            stakingAmount: amount,
            claimedAmount: data.data?.claimedAmount || 0,
            newBalance: parseFloat(data.data?.newBalance || userBalance),
            stakingType: selectedStaking || 'lunar'
          });

          // Открываем модальное окно успеха
          setSuccessModalOpen(true);

          // Обновляем баланс в сторе
          if (data.data?.newBalance) {
            updateBalance(parseFloat(data.data.newBalance));
            setUserBalance(parseFloat(data.data.newBalance));
          }

          // Обновляем данные
          await fetchUserData();
          await fetchUserDeposits();

          // Обновляем last_claim в сторе для сброса прогресс-бара
          if (data.data?.nextClaimTime) {
            if (user) {
              setUser({
                ...user,
                lastClaim: new Date(),
                balance: parseFloat(data.data.newBalance)
              });
            }
          }
        } else {
          const error = await response.json();
          alert(error.message || t('staking.createError'));
        }
      } catch (error) {
        console.error('Ошибка создания депозита:', error);
        alert(t('staking.generalError'));
      }
    }

    // Оплата через TON Keeper
    if (paymentMethod === 'tonkeeper') {
      await handleTonPaymentStaking(amount, selectedStaking);
    }
  };

  // Проверка достаточности TON для газа (v4r2 кошельки требуют ~0.35 TON)
  const checkTonBalance = async (): Promise<boolean> => {
    if (!tonAddress) return true;

    try {
      const response = await fetch(`https://tonapi.io/v2/accounts/${tonAddress}`);
      const data = await response.json();
      const balance = parseInt(data.balance || '0') / 1e9;
      console.log('TON balance check:', balance, 'TON');

      if (balance < 0.35) {
        alert(t('staking.insufficientTonGas', { balance: balance.toFixed(2) }));
        return false;
      }
      return true;
    } catch (e) {
      console.warn('Не удалось проверить баланс TON:', e);
      return true; // Пропускаем проверку при ошибке API
    }
  };

  // Функция оплаты стейкинга через TON Keeper
  const handleTonPaymentStaking = async (amountSperm: number, stakingType: 'lunar' | 'star') => {
    // Проверяем подключен ли кошелёк
    if (!tonAddress) {
      try {
        await tonConnectUI.openModal();
      } catch (e) {
        console.log('Модальное окно закрыто');
      }
      return;
    }

    // Проверяем достаточность TON для газа (важно для v4r2 кошельков)
    const hasEnoughTon = await checkTonBalance();
    if (!hasEnoughTon) {
      return;
    }

    try {
      // Рассчитываем сумму в TON (SPERM → TON)
      // Используем реальные курсы из store
      const tonPriceUsd = tonPrice || 1.55;
      const spermPriceUsd = spermPrice || 0.40;
      // +1% запас для покрытия slippage при свопе через STON.fi
      // Чтобы минималка 50 SPERM точно прошла даже при небольшом изменении курса
      const SLIPPAGE_BUFFER = 1.01; // +1%
      const amountTon = ((amountSperm * spermPriceUsd) / tonPriceUsd) * SLIPPAGE_BUFFER;

      // Кошелёк проекта для получения TON
      const PROJECT_WALLET = 'UQAvK4BqL9ySHgaRaQ25Vd5c7LcgpX7ypoNEnATO3RtDRIs8';

      // Комментарий для идентификации платежа
      const telegramId = user?.telegramId || user?.id || 'unknown';
      const comment = `STAKING_${stakingType.toUpperCase()}_${telegramId}_${amountSperm}`;
      console.log('TON Staking comment:', comment);

      // Формируем payload с комментарием (простая отправка TON)
      const payloadCell = beginCell()
        .storeUint(0, 32) // 0 opcode = текстовый комментарий
        .storeStringTail(comment)
        .endCell();

      const payload = payloadCell.toBoc().toString('base64');

      // Структура транзакции
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут (увеличено для v4r2 кошельков)
        messages: [
          {
            address: PROJECT_WALLET,
            amount: toNano(amountTon.toFixed(4)).toString(), // Сумма в nanoTON
            payload: payload
          }
        ]
      };

      console.log('Sending TON transaction:', {
        amount: amountTon.toFixed(4),
        spermEquivalent: amountSperm,
        stakingType,
        comment
      });

      // Отправляем транзакцию
      const result = await tonConnectUI.sendTransaction(transaction);

      if (result && result.boc) {
        console.log('TON Transaction sent, BOC:', result.boc);

        // Транзакция отправлена! Стейкинг будет создан автоматически при получении TON
        // WalletMonitor обрабатывает входящие транзакции и создаёт стейкинг через STON.fi роутер

        // Закрываем модальное окно
        setModalOpen(false);
        setSelectedStaking(null);

        // Показываем сообщение об успешной отправке
        alert(`${t('staking.txSent')}\n\n${t('staking.tonSent', { amount: amountTon.toFixed(4) })}\n${t('staking.stakingAutoCreate')}\n${t('staking.spermByRate')}`);

        // Обновляем данные через 5 секунд (дать время на обработку)
        setTimeout(async () => {
          await fetchUserData();
          await fetchUserDeposits();
        }, 5000);
      }
    } catch (error: any) {
      console.error('Ошибка TonConnect:', error);

      const errorMsg = error?.message?.toLowerCase() || '';
      if (errorMsg.includes('cancel') || errorMsg.includes('reject') || errorMsg.includes('denied') || errorMsg.includes('user')) {
        console.log('Транзакция отменена пользователем');
      } else if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
        alert(t('staking.insufficientTon'));
      } else {
        alert(t('staking.txError'));
      }
    }
  };

  // Подтверждение марафона - оплата в USDT + сжигание SPERM (36.6%)
  const handleConfirmMarathon = async (amountUsdt: number, paymentMethod: PaymentMethod, burnSpermAmount: number, burnSource: BurnSource = 'balance') => {
    console.log('handleConfirmMarathon:', { amountUsdt, paymentMethod, burnSpermAmount, burnSource });

    // Если TON Keeper - отправляем USDT через TonConnect
    if (paymentMethod === 'tonkeeper') {
      // Проверяем подключен ли кошелёк
      if (!tonAddress) {
        try {
          await tonConnectUI.openModal();
        } catch (e) {
          console.log('Модальное окно закрыто');
        }
        return;
      }

      // Запускаем депозит USDT через TonConnect
      setMarathonAmount(amountUsdt.toString());
      setMarathonModalOpen(false);
      handleMarathonDepositFromModal(amountUsdt, burnSpermAmount, burnSource);
      return;
    }

    // Для usdt_balance - списываем USDT с основного баланса + сжигаем SPERM
    if (paymentMethod === 'usdt_balance') {
      try {
        const response = await fetch('/api/staking/create-marathon-from-usdt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            amountUsdt: amountUsdt,
            burnSpermAmount: burnSpermAmount,
            source: 'usdt_balance',
            burnSource: burnSource
          })
        });

        if (response.ok) {
          const data = await response.json();

          // Закрываем модальное окно марафона
          setMarathonModalOpen(false);

          // Показываем успех
          setMarathonCreatedAmount(amountUsdt);
          setMarathonCreatedOpen(true);

          // Обновляем данные
          await fetchUserData();
          await fetchUserDeposits();
        } else {
          const error = await response.json();
          alert(error.message || t('staking.createError'));
        }
      } catch (error) {
        console.error('Ошибка создания марафона из USDT:', error);
        alert(t('staking.generalError'));
      }
      return;
    }

    // Для promo - открываем марафон за акционный бонус (Lunar Start) + сжигаем SPERM
    if (paymentMethod === 'promo') {
      try {
        const response = await fetch('/api/staking/create-marathon-from-usdt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            amountUsdt: amountUsdt,
            burnSpermAmount: burnSpermAmount,
            source: 'promo',
            burnSource: burnSource
          })
        });

        if (response.ok) {
          const data = await response.json();
          setMarathonModalOpen(false);
          setMarathonCreatedAmount(amountUsdt);
          setMarathonCreatedOpen(true);
          await fetchUserData();
          await fetchUserDeposits();
        } else {
          const error = await response.json();
          alert(error.message || t('staking.createError'));
        }
      } catch (error) {
        console.error('Ошибка создания акционного марафона:', error);
        alert(t('staking.generalError'));
      }
      return;
    }

    // Открытие из копилки убрано: профит марафонов нельзя пускать на
    // открытие новых (смешивание пула). Источники — баланс/кошелёк/промо.
  };

  // Функция для депозита марафона через TonConnect USDT (вызывается из handleConfirmMarathon)
  const handleMarathonDepositFromModal = async (amountUsdt: number, burnSpermAmount: number, burnSource: BurnSource = 'balance') => {
    // Проверяем минимальную сумму
    if (amountUsdt < 50) {
      alert(t('marathonModal.minAmount', { amount: 50 }));
      return;
    }

    // Проверяем подключен ли кошелёк
    if (!tonAddress) {
      try {
        await tonConnectUI.openModal();
      } catch (e) {
        console.log('Модальное окно закрыто');
      }
      return;
    }

    setMarathonLoading(true);

    // Проверяем достаточность TON для газа (важно для v4r2 кошельков)
    const hasEnoughTon = await checkTonBalance();
    if (!hasEnoughTon) {
      setMarathonLoading(false);
      return;
    }

    try {
      // USDT Jetton контракт на TON
      const USDT_JETTON_MASTER = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';
      // Кошелёк проекта (приёмник USDT)
      const PROJECT_WALLET = 'UQAvK4BqL9ySHgaRaQ25Vd5c7LcgpX7ypoNEnATO3RtDRIs8';

      // Получаем адрес Jetton wallet пользователя для USDT
      const userAddress = Address.parse(tonAddress);

      // Формируем комментарий: MARATHON_USDT_telegramId_amountUsdt_burnSperm_burnSource
      const telegramId = user?.telegramId || user?.id || 'unknown';
      const comment = `MARATHON_USDT_${telegramId}_${amountUsdt}_${Math.round(burnSpermAmount)}_${burnSource}`;
      console.log('Marathon USDT comment:', comment);

      // Сумма USDT в нано (6 decimals)
      const usdtAmountNano = BigInt(Math.floor(amountUsdt * 1_000_000));

      // Формируем payload для Jetton transfer
      // opcode 0xf8a7ea5 = jetton transfer
      const forwardPayload = beginCell()
        .storeUint(0, 32) // opcode 0 = текстовый комментарий
        .storeStringTail(comment)
        .endCell();

      const jettonTransferBody = beginCell()
        .storeUint(0xf8a7ea5, 32) // opcode for jetton transfer
        .storeUint(0, 64) // query_id
        .storeCoins(usdtAmountNano) // amount of jettons to transfer
        .storeAddress(Address.parse(PROJECT_WALLET)) // destination address
        .storeAddress(userAddress) // response destination (refund address)
        .storeBit(0) // no custom payload
        .storeCoins(toNano('0.05')) // forward TON amount
        .storeBit(1) // forward payload in ref
        .storeRef(forwardPayload)
        .endCell();

      // Получаем адрес jetton wallet пользователя через toncenter API
      // (tonapi.io возвращает raw-адрес, который не принимается TonConnect/STON Keeper)
      let userJettonWalletAddress: string;
      try {
        const response = await fetch(
          `https://toncenter.com/api/v3/jetton/wallets?owner_address=${tonAddress}&jetton_address=${USDT_JETTON_MASTER}&limit=1`
        );
        const jettonData = await response.json();

        if (jettonData.jetton_wallets && jettonData.jetton_wallets.length > 0) {
          const rawAddress = jettonData.jetton_wallets[0].address;
          // Используем user-friendly адрес из address_book (обязателен для TonConnect!)
          if (jettonData.address_book && jettonData.address_book[rawAddress]) {
            userJettonWalletAddress = jettonData.address_book[rawAddress].user_friendly;
          } else {
            // Конвертируем raw → user-friendly через Address.parse
            userJettonWalletAddress = Address.parse(rawAddress).toString();
          }
          console.log('User USDT Jetton Wallet (raw):', rawAddress);
          console.log('User USDT Jetton Wallet (friendly):', userJettonWalletAddress);
        } else {
          throw new Error('Не найден USDT wallet');
        }
      } catch (e) {
        console.error('Ошибка получения Jetton Wallet:', e);
        alert(t('staking.noUsdtWallet'));
        setMarathonLoading(false);
        return;
      }

      // Структура транзакции
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут (увеличено для v4r2 кошельков)
        messages: [
          {
            address: userJettonWalletAddress, // user-friendly адрес jetton wallet
            amount: toNano('0.1').toString(), // TON для gas
            payload: jettonTransferBody.toBoc().toString('base64')
          }
        ]
      };

      console.log('Sending Marathon USDT transaction:', {
        to: PROJECT_WALLET,
        jettonWallet: userJettonWalletAddress,
        amount: amountUsdt + ' USDT',
        burnSperm: Math.round(burnSpermAmount),
        burnSource: burnSource,
        comment: comment
      });

      // Отправляем транзакцию
      const result = await tonConnectUI.sendTransaction(transaction);

      if (result && result.boc) {
        console.log('Marathon USDT Transaction sent, BOC:', result.boc);

        // Вызываем бэкенд для создания марафона и сжигания SPERM
        try {
          const backendResponse = await fetch('/api/staking/create-marathon', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              amount: amountUsdt,
              txHash: result.boc,
              walletAddress: tonAddress,
              paymentId: comment,
              burnSource: burnSource
            })
          });

          if (backendResponse.ok) {
            const data = await backendResponse.json();
            setMarathonCreatedAmount(amountUsdt);
            setMarathonCreatedOpen(true);
          } else {
            const errorData = await backendResponse.json();
            // Транзакция уже отправлена - показываем частичный успех
            alert(`${t('staking.txSent')}\n\n${t('staking.usdtSent', { amount: amountUsdt })}\n\n${errorData.message || t('staking.processingTime')}`);
          }
        } catch (backendError) {
          console.error('Ошибка вызова бэкенда после TonConnect:', backendError);
          // USDT уже отправлена - показываем сообщение о обработке
          alert(`${t('staking.txSent')}\n\n${t('staking.usdtSent', { amount: amountUsdt })}\n${t('staking.burnAmount', { amount: Math.round(burnSpermAmount) })}\n\n${t('staking.processingTime')}`);
        }

        // Закрываем модальное окно и обновляем данные
        setMarathonModalOpen(false);

        // Обновляем данные
        setTimeout(async () => {
          await fetchUserDeposits();
          await fetchUserData();
        }, 3000);
      }
    } catch (error: any) {
      console.error('Ошибка TonConnect Marathon USDT:', error);
      console.error('Полная ошибка:', JSON.stringify(error, null, 2));

      const errorMsg = error?.message?.toLowerCase() || '';
      if (errorMsg.includes('cancel') || errorMsg.includes('reject') || errorMsg.includes('denied') || errorMsg.includes('user')) {
        console.log('Транзакция отменена пользователем');
      } else if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
        alert(t('staking.insufficientUsdtOrTon'));
      } else {
        alert(`${t('staking.txError')}\n\n${error?.message || 'Unknown error'}`);
      }
    } finally {
      setMarathonLoading(false);
    }
  };

  // 💵 Депозит в лунный марафон через TonConnect USDT
  const handleMarathonDeposit = async () => {
    if (!marathonAmount || parseFloat(marathonAmount) < 50) {
      alert(t('marathonModal.minAmount', { amount: 50 }));
      return;
    }

    // Проверяем подключен ли кошелёк
    if (!tonAddress) {
      try {
        await tonConnectUI.openModal();
      } catch (e) {
        console.log('Модальное окно закрыто');
      }
      return;
    }

    setMarathonLoading(true);

    // Проверяем достаточность TON для газа (важно для v4r2 кошельков)
    const hasEnoughTon = await checkTonBalance();
    if (!hasEnoughTon) {
      setMarathonLoading(false);
      return;
    }

    try {
      const amountInUsdt = parseFloat(marathonAmount);

      // USDT Master контракт в TON mainnet
      const USDT_MASTER = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';

      // Кошелёк проекта для получения USDT
      const PROJECT_WALLET = 'UQAvK4BqL9ySHgaRaQ25Vd5c7LcgpX7ypoNEnATO3RtDRIs8';

      // Комментарий для идентификации платежа
      const telegramId = user?.telegramId || user?.id || 'unknown';
      const comment = `MARATHON_${telegramId}`;
      console.log('Marathon USDT comment:', comment);

      // ШАГ 1: Получаем адрес Jetton Wallet пользователя через API
      // Jetton Wallet - это контракт, который хранит баланс USDT конкретного пользователя
      let userJettonWallet: string;

      try {
        // Используем TON Center API для получения jetton wallet адреса
        const jettonWalletResponse = await fetch(
          `https://toncenter.com/api/v3/jetton/wallets?owner_address=${tonAddress}&jetton_address=${USDT_MASTER}&limit=1`
        );
        const jettonData = await jettonWalletResponse.json();

        if (jettonData.jetton_wallets && jettonData.jetton_wallets.length > 0) {
          // API возвращает raw адрес (0:xxxx), нужно взять user_friendly из address_book
          const rawAddress = jettonData.jetton_wallets[0].address;

          // Проверяем address_book для получения user-friendly адреса
          if (jettonData.address_book && jettonData.address_book[rawAddress]) {
            userJettonWallet = jettonData.address_book[rawAddress].user_friendly;
          } else {
            // Если нет в address_book, используем raw адрес как есть
            userJettonWallet = rawAddress;
          }

          console.log('User USDT Jetton Wallet (raw):', rawAddress);
          console.log('User USDT Jetton Wallet (friendly):', userJettonWallet);
        } else {
          alert(t('staking.noUsdtBalance'));
          setMarathonLoading(false);
          return;
        }
      } catch (apiError) {
        console.error('Ошибка получения Jetton Wallet:', apiError);
        alert(t('staking.walletDataError'));
        setMarathonLoading(false);
        return;
      }

      // ШАГ 2: Формируем forward_payload с комментарием (TEP-74)
      const forwardPayload = beginCell()
        .storeUint(0, 32) // 0 opcode = текстовый комментарий
        .storeStringTail(comment)
        .endCell();

      // ШАГ 3: Формируем тело jetton transfer (TEP-74 стандарт)
      // USDT имеет 6 decimals, поэтому умножаем на 1e6
      const jettonAmount = BigInt(Math.floor(amountInUsdt * 1e6));

      const body = beginCell()
        .storeUint(0xf8a7ea5, 32)                              // op code для jetton transfer
        .storeUint(0, 64)                                       // query_id
        .storeCoins(jettonAmount)                               // amount в микро USDT (6 decimals)
        .storeAddress(Address.parse(PROJECT_WALLET))            // destination - кошелёк проекта
        .storeAddress(Address.parse(tonAddress))                // response_destination - возврат излишков
        .storeBit(0)                                            // custom_payload отсутствует
        .storeCoins(toNano('0.01'))                             // forward_ton_amount - минимум 0.01 TON для доставки уведомления!
        .storeBit(1)                                            // forward_payload как reference
        .storeRef(forwardPayload)                               // сам комментарий
        .endCell();

      const payload = body.toBoc().toString('base64');
      console.log('Generated USDT jetton transfer payload');
      console.log('Amount:', amountInUsdt, 'USDT (', jettonAmount.toString(), 'micro)');
      console.log('Destination:', PROJECT_WALLET);
      console.log('Comment:', comment);

      // ШАГ 4: Структура транзакции - отправляем НА JETTON WALLET пользователя
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут (увеличено для v4r2 кошельков)
        messages: [
          {
            address: userJettonWallet,                    // ВАЖНО: адрес Jetton Wallet пользователя, НЕ мастер контракта!
            amount: toNano('0.1').toString(),             // 0.1 TON на комиссии (0.05 transfer + 0.01 forward + запас)
            payload: payload
          }
        ]
      };

      console.log('Sending USDT transaction to Jetton Wallet:', userJettonWallet);

      // Отправляем транзакцию
      const result = await tonConnectUI.sendTransaction(transaction);

      if (result && result.boc) {
        console.log('USDT Transaction sent, BOC:', result.boc);

        // Транзакция успешна - создаём депозит на backend
        const response = await fetch('/api/staking/create-marathon', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            amount: amountInUsdt,
            txHash: result.boc,
            walletAddress: tonAddress,
            paymentId: comment
          })
        });

        if (response.ok) {
          setMarathonCreatedAmount(amountInUsdt);
          setMarathonCreatedOpen(true);
          setMarathonAmount('');
          await fetchUserDeposits();
        } else {
          const error = await response.json();
          alert(error.message || t('staking.createError'));
        }
      }
    } catch (error: any) {
      console.error('Ошибка TonConnect:', error);

      const errorMsg = error?.message?.toLowerCase() || '';
      if (errorMsg.includes('cancel') || errorMsg.includes('reject') || errorMsg.includes('denied') || errorMsg.includes('user')) {
        console.log('Транзакция отменена пользователем');
      } else if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
        alert(t('staking.insufficientUsdtOrTon'));
      } else {
        alert(t('staking.txError'));
      }
    } finally {
      setMarathonLoading(false);
    }
  };

  const handleStarWithdraw = (deposit: any) => {
    // Звук воспроизводится в модалке при открытии (playModalOpen)
    setSelectedDepositForWithdraw(deposit);
    setStarWithdrawModalOpen(true);
  };

  const handleConfirmStarWithdraw = async (depositId: number, amount: number) => {
    try {
      const response = await fetch('/api/staking/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          stakingId: depositId,
          amount
        })
      });

      if (response.ok) {
        const data = await response.json();
        const result = data.data;

        // Закрываем модальное окно вывода
        setStarWithdrawModalOpen(false);
        setSelectedDepositForWithdraw(null);

        // Показываем детальное сообщение об успехе
        const received = result?.received?.toFixed(2) || amount;
        const penalty = result?.penalty?.toFixed(2) || 0;
        const penaltyPercent = result?.penaltyPercent || 0;
        const isPartial = result?.isPartial;
        const remaining = result?.remainingAmount?.toFixed(2);

        let successMessage = `${t('staking.withdrawnSuccess', { amount: amount.toLocaleString() })}\n`;
        successMessage += `${t('staking.withdrawPenalty', { percent: penaltyPercent, amount: penalty })}\n`;
        successMessage += t('staking.withdrawReceived', { amount: received });

        if (isPartial && remaining) {
          successMessage += `\n\n${t('staking.remainingInStaking', { amount: remaining })}\n`;
          successMessage += t('staking.stakingContinues');
        }

        alert(successMessage);

        // Обновляем баланс
        if (result?.newBalance) {
          updateBalance(parseFloat(result.newBalance));
          setUserBalance(parseFloat(result.newBalance));
        }

        // Обновляем данные
        await fetchUserData();
        await fetchUserDeposits();
      } else {
        const error = await response.json();
        alert(error.message || t('staking.withdrawError'));
      }
    } catch (error) {
      console.error('Ошибка вывода средств:', error);
      alert(t('staking.generalError'));
    }
  };

  // 🎯 Забрать прибыль марафона за текущий период
  const handleClaimMarathonProfit = async (deposit: any) => {
    const marathonId = deposit.id;
    // 🛡️ Синхронный замок: если запрос по этому марафону уже летит — выходим.
    // Без этого частые тапы шлют параллельные claim'ы и они дерутся за
    // блокировку строки users (lock timeout), claim не проходит.
    if (marathonBusyRef.current.has(marathonId)) return;
    marathonBusyRef.current.add(marathonId);
    setMarathonBusy(prev => ({ ...prev, [marathonId]: true }));
    // 🛡️ Таймаут запроса: если бэк не ответит за 30с — прерываем fetch. Иначе
    // promise не зарезолвится, finally не снимет замок marathonBusyRef, и кнопка
    // зависнет в «Обработка...» навсегда (так и было при крашах бэка).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      SoundManager.playButtonClick();

      const response = await fetch('/api/staking/claim-marathon-profit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          stakingId: deposit.id
        }),
        signal: controller.signal
      });

      const data = await response.json();

      if (response.ok && data.success) {
        SoundManager.playClaimSuccess();

        // Вибрация при успешном заборе прибыли
        if (navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }

        // Показываем красивое модальное уведомление
        const claimedAmount = data.data.claimedAmount?.toFixed(2) || '0.00';
        const message = data.data.isCompleted
          ? `🎉 ${t('staking.marathonCompleted', { amount: claimedAmount, period: data.data.period })}`
          : `✅ ${t('staking.marathonProfitClaimed', { amount: claimedAmount })}`;

        // Используем toast/notification вместо alert
        setNotificationMessage(message);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);

        // Обновляем данные
        await fetchUserDeposits();
      } else {
        alert(data.message || t('staking.profitClaimError'));
      }
    } catch (error) {
      console.error('Ошибка claim marathon profit:', error);
      alert((error as any)?.name === 'AbortError'
        ? 'Сервер не ответил вовремя. Попробуйте ещё раз.'
        : t('staking.generalError'));
    } finally {
      clearTimeout(timeoutId);
      marathonBusyRef.current.delete(marathonId);
      setMarathonBusy(prev => {
        const next = { ...prev };
        delete next[marathonId];
        return next;
      });
    }
  };

  // 💸 Вывод накопленной прибыли конкретного марафона в копилку (на вывод)
  const handleWithdrawMarathonProfit = async (deposit: any) => {
    const marathonId = deposit.id;
    if (marathonBusyRef.current.has(marathonId)) return;
    marathonBusyRef.current.add(marathonId);
    setMarathonBusy(prev => ({ ...prev, [marathonId]: true }));
    // 🛡️ Таймаут запроса (см. claim выше) — чтобы замок не залипал навсегда.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      SoundManager.playButtonClick();
      const response = await fetch('/api/staking/withdraw-marathon-profit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ stakingId: deposit.id }),
        signal: controller.signal
      });
      const data = await response.json();
      if (response.ok && data.success) {
        SoundManager.playClaimSuccess();
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        setNotificationMessage(`✅ ${data.message || 'Прибыль переведена в копилку'}`);
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 4000);
        await fetchUserDeposits();
        await fetchUserData();
      } else {
        alert(data.message || 'Не удалось вывести прибыль');
      }
    } catch (error) {
      console.error('Ошибка вывода прибыли марафона:', error);
      alert((error as any)?.name === 'AbortError'
        ? 'Сервер не ответил вовремя. Попробуйте ещё раз.'
        : t('staking.generalError'));
    } finally {
      clearTimeout(timeoutId);
      marathonBusyRef.current.delete(marathonId);
      setMarathonBusy(prev => {
        const next = { ...prev };
        delete next[marathonId];
        return next;
      });
    }
  };

  // 🎟️ Активация промокода (акция Lunar Start)
  const handleActivatePromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    try {
      const response = await fetch('/api/promo/activate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const bonus = data.data?.bonusUsdt || 50;
        const usedCode = data.data?.code || code.toUpperCase();
        setPromoCode('');
        setPromoOpen(false);
        // Праздничная модалка (она сама играет звук + вибро)
        setPromoSuccessAmount(bonus);
        setPromoSuccessCode(usedCode);
        setPromoSuccessOpen(true);
        await fetchUserData();
      } else {
        alert(data.message || 'Не удалось активировать промокод');
      }
    } catch (error) {
      console.error('Ошибка активации промокода:', error);
      alert(t('staking.generalError'));
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="staking-page">
      {/* Уведомление об успешном заборе прибыли */}
      {showSuccessNotification && (
        <div className="marathon-success-toast">
          <div className="toast-content">
            <span className="toast-icon">💰</span>
            <span className="toast-message">{notificationMessage}</span>
          </div>
        </div>
      )}

      <div className="page-content">
        {/* 🎟️ Активация промокода (акция Lunar Start) */}
        <div style={{ marginBottom: '14px' }}>
          {!promoOpen ? (
            <button
              onClick={() => { SoundManager.playButtonClick(); setPromoOpen(true); }}
              style={{
                width: '100%', padding: '12px', borderRadius: '12px',
                border: '1px solid rgba(38,161,123,0.5)',
                background: 'rgba(38,161,123,0.12)', color: '#26a17b',
                fontSize: '15px', fontWeight: 600, cursor: 'pointer'
              }}
            >
              🎟️ {t('staking.activatePromo', 'Активировать промокод')}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder={t('staking.promoPlaceholder', 'Введите промокод')}
                maxLength={64}
                autoFocus
                style={{
                  flex: 1, padding: '12px', borderRadius: '12px',
                  border: '1px solid rgba(38,161,123,0.5)',
                  background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: '15px'
                }}
              />
              <button
                onClick={handleActivatePromo}
                disabled={promoLoading || !promoCode.trim()}
                style={{
                  padding: '12px 16px', borderRadius: '12px', border: 'none',
                  background: (promoLoading || !promoCode.trim()) ? '#555' : '#26a17b',
                  color: '#fff', fontSize: '15px', fontWeight: 600,
                  cursor: (promoLoading || !promoCode.trim()) ? 'default' : 'pointer'
                }}
              >
                {promoLoading ? '...' : t('staking.activate', 'Активировать')}
              </button>
              <button
                onClick={() => { setPromoOpen(false); setPromoCode(''); }}
                style={{
                  padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent', color: '#aaa', cursor: 'pointer'
                }}
              >✕</button>
            </div>
          )}
        </div>

        {/* Статистика стейкинга */}
        <div className="staking-stats">
          {/* SPERM статистика */}
          <div className="stat-card sperm-stat">
            <span className="stat-icon">🪙</span>
            <div className="stat-info">
              <span className="stat-value">{formatNumber(stakingStats.totalStaked, 0)}</span>
              <span className="stat-label">{t('staking.inStakingSperm')}</span>
              <span className="stat-sublabel">({t('staking.deposit')}: {formatNumber(stakingStats.totalRealStaked, 0)})</span>
            </div>
          </div>
          <div className="stat-card sperm-stat">
            <span className="stat-icon">📈</span>
            <div className="stat-info">
              <span className="stat-value">+{formatNumber(stakingStats.dailyIncome, 1)}{t('staking.perDay')}</span>
              <span className="stat-label">{t('staking.incomeSperm')}</span>
              {(stakingStats.lunarDailyIncome > 0 || stakingStats.starDailyIncome > 0) && (
                <span className="stat-breakdown">
                  {stakingStats.lunarDailyIncome > 0 && `🌙 ${formatNumber(stakingStats.lunarDailyIncome, 1)}`}
                  {stakingStats.lunarDailyIncome > 0 && stakingStats.starDailyIncome > 0 && ' | '}
                  {stakingStats.starDailyIncome > 0 && `⭐ ${formatNumber(stakingStats.starDailyIncome, 1)}`}
                </span>
              )}
            </div>
          </div>

          {/* USDT статистика (Марафон) */}
          <div className="stat-card usdt-stat">
            <span className="stat-icon">💵</span>
            <div className="stat-info">
              <span className="stat-value">{formatNumber(stakingStats.totalStakedUsdt, 0)} USDT</span>
              <span className="stat-label">{t('staking.inMarathon')}</span>
            </div>
          </div>
          <div className="stat-card usdt-stat">
            <span className="stat-icon">🎯</span>
            <div className="stat-info">
              <span className="stat-value">+{formatNumber(stakingStats.dailyIncomeUsdt, 2)}{t('staking.perDay')}</span>
              <span className="stat-label">{t('staking.incomeUsdt')}</span>
            </div>
          </div>

          {/* Общая статистика */}
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-info">
              <span className="stat-value">{stakingStats.activeDeposits}</span>
              <span className="stat-label">{t('staking.activeDeposits')}</span>
            </div>
          </div>
        </div>

        {/* Копилка - между статистикой и вкладками */}
        <PiggyBank />

        {/* Переключатель тарифов - 3 вкладки с полными названиями в 2 строки */}
        <div className="staking-tabs">
          {/* Лунный Марафон — теперь первая (основная) вкладка */}
          <button
            className={`staking-tab ${activeTab === 'marathon' ? 'active' : ''}`}
            onClick={() => { SoundManager.playButtonClick(); setActiveTab('marathon'); }}
          >
            <span className="tab-text">
              <span>{t('staking.marathonTabLine1')}</span>
              <span>{t('staking.marathonTabLine2')}</span>
            </span>
            <span
              className="help-icon"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                SoundManager.playButtonClick();
                setShowInfoModal('marathon');
              }}
            >
              ?
            </span>
          </button>
          {/* Лунный и Звёздный — справа, только для управления уже открытыми (пул исчерпан) */}
          <button
            className={`staking-tab ${activeTab === 'lunar' ? 'active' : ''}`}
            onClick={() => { SoundManager.playButtonClick(); setActiveTab('lunar'); }}
          >
            <span className="tab-text">
              <span>{t('staking.lunarTabLine1')}</span>
              <span>{t('staking.lunarTabLine2')}</span>
            </span>
            <span
              className="help-icon"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                SoundManager.playButtonClick();
                setShowInfoModal('lunar');
              }}
            >
              ?
            </span>
          </button>
          <button
            className={`staking-tab ${activeTab === 'star' ? 'active' : ''}`}
            onClick={() => { SoundManager.playButtonClick(); setActiveTab('star'); }}
          >
            <span className="tab-text">
              <span>{t('staking.starTabLine1')}</span>
              <span>{t('staking.starTabLine2')}</span>
            </span>
            <span
              className="help-icon"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                SoundManager.playButtonClick();
                setShowInfoModal('star');
              }}
            >
              ?
            </span>
          </button>
        </div>
        
        {/* Фиксированная секция выбора типа стейкинга - ВСЕГДА показываем только эти 2 карточки */}
        <div className="staking-options-section">
          {activeTab === 'marathon' && (
            <h2 className="section-title">{t('staking.createNewStaking')}</h2>
          )}
          <div className="staking-options-grid">
            {/* Лунный/Звёздный — пул исчерпан: открыть новый стейкинг нельзя.
                Управление уже открытыми депозитами — в списке ниже. */}
            {(activeTab === 'lunar' || activeTab === 'star') && (
              <div className="staking-card" style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔒</div>
                <h3 className="staking-title" style={{ marginBottom: '10px' }}>
                  {activeTab === 'lunar' ? t('staking.lunarStakingTitle') : t('staking.starStakingTitle')}
                </h3>
                <p style={{ color: '#ff9800', fontSize: '14px', lineHeight: 1.5, margin: 0 }}>
                  {t('staking.poolFinishedNotice', 'Пул исчерпан — открыть новый стейкинг больше нельзя. Ниже — ваши активные стейкинги: доход и вывод работают как обычно.')}
                </p>
              </div>
            )}

            {/* Лунный Марафон - единственный доступный для открытия */}
            <div className={`staking-card marathon-staking ${activeTab !== 'marathon' ? 'hidden' : ''}`}>
              <div className="staking-header">
                <span className="staking-icon">🎯</span>
                <h3 className="staking-title">{t('staking.lunarMarathon')}</h3>
                <span className="staking-badge marathon usdt">💵 USDT</span>
              </div>

              <div className="staking-info">
                <div className="info-row">
                  <span className="info-label">{t('staking.marathonPeriodDuration')}:</span>
                  <span className="info-value">{t('staking.marathonPeriodDays')}</span>
                </div>
                <div className="info-row highlight-row">
                  <span className="info-label">{t('staking.marathonTotalRoi')}:</span>
                  <span className="info-value roi-highlight">315% 🚀</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('staking.marathonTotalPeriods')}:</span>
                  <span className="info-value">{t('staking.marathonPeriodsValue')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('staking.marathonBurnSperm')}:</span>
                  <span className="info-value burn-value">{t('staking.marathonBurnValue')} 🔥</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('staking.marathonMinDeposit')}:</span>
                  <span className="info-value usdt-value">💵 50 USDT</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t('staking.marathonPaymentMethods')}:</span>
                  <span className="info-value">{t('staking.marathonPaymentOptions')}</span>
                </div>
              </div>

              <button
                className="staking-btn create-btn marathon-btn"
                onClick={() => {
                  // Звук играется в MarathonModal useEffect (playModalOpen)
                  setMarathonModalOpen(true);
                }}
              >
                🎯 {t('staking.openMarathon')}
              </button>
            </div>
          </div>
        </div>

        {/* Burn виджет (счётчик «Монет осталось» убран — пулы исчерпаны, вводил в заблуждение) */}
        <BurnWidget />

        {/* Активные депозиты - отдельная секция внизу */}
        <div className="deposits-section">
          <h3 className="section-title">
            {activeTab === 'marathon'
              ? t('staking.activeMarathonsTitle')
              : t('staking.activeDepositsTitle')
            } ({userDeposits.filter(d => (d.staking_type || d.type) === activeTab && (d.status === 'active' || d.status === 'paused')).length})
          </h3>
          {userDeposits.filter(d => (d.staking_type || d.type) === activeTab && (d.status === 'active' || d.status === 'paused')).length === 0 ? (
            <div className="no-deposits">
              <span className="no-deposits-icon">📭</span>
              <p>{t('staking.noDeposits')}</p>
            </div>
          ) : (
            <div className="deposits-list compact">
              {userDeposits.filter(d => (d.staking_type || d.type) === activeTab && (d.status === 'active' || d.status === 'paused')).map((deposit, index) => {
                const isExpanded = expandedDeposit === (deposit.id || index);
                // API возвращает staking_type, а не type
                const depositType = deposit.staking_type || deposit.type;
                const amount = parseFloat(deposit.amount) || 0;
                const actualAmount = parseFloat(deposit.actual_amount) || amount; // Реальная сумма после burn
                const visualBalanceRaw = parseFloat(deposit.visual_balance) || 0;
                const currentApr = parseFloat(deposit.effective_apr || deposit.current_apr) || (depositType === 'lunar' ? 1.0 : 0.1);

                // Для лунных депозитов - визуальный баланс из БД
                const isLunar = depositType === 'lunar';
                const visualBalance = isLunar ? (visualBalanceRaw || amount * 1.8) : amount;
                // Начальный visual = текущий + заработанное (шкала всегда стартует с 100%)
                const totalEarnedDeposit = parseFloat(deposit.total_earned) || 0;
                const initialVisualBalance = isLunar ? (visualBalance + totalEarnedDeposit) || amount * 1.8 : amount;

                // ✅ ИСПРАВЛЕНО: Для lunar доход считается от VISUAL_BALANCE (eternal regression)
                // Бэкенд: dailyProfit = visualBalance * (effectiveApr / 100)
                const isMarathon = depositType === 'marathon';
                let dailyIncome: number;

                // Для star и marathon используем income_base_amount (реальный amount)
                const incomeBase = deposit.income_base_amount
                  ? parseFloat(deposit.income_base_amount)
                  : amount; // Fallback на amount

                // 🆕 Рассчитываем возраст депозита для всех типов (нужно для расчёта периода марафона)
                const depositAge = Math.floor((Date.now() - new Date(deposit.created_at).getTime()) / (1000 * 60 * 60 * 24));

                if (isMarathon) {
                  // Марафон: процент зависит от периода (из GitBook)
                  const MARATHON_PAYOUT_RATES_LOCAL: Record<number, number> = {
                    1: 30.0, 2: 27.9, 3: 26.0, 4: 24.3, 5: 22.7,
                    6: 21.3, 7: 20.0, 8: 19.1, 9: 17.9, 10: 16.9,
                    11: 15.9, 12: 15.1, 13: 14.3, 14: 13.5, 15: 12.9,
                    16: 12.2, 17: 11.6, 18: 11.1, 19: 10.6, 20: 10.2
                  };
                  // Период берём из бэкенда (subscription_period)
                  const backendPeriod = deposit.subscription_period || 1;
                  // Если марафон заморожен — показываем доход предыдущего периода (база ещё без довнесения)
                  const currentPeriodForRate = deposit.is_frozen && backendPeriod > 1 ? backendPeriod - 1 : backendPeriod;
                  const payoutPercent = MARATHON_PAYOUT_RATES_LOCAL[currentPeriodForRate] || 9.7;
                  dailyIncome = (incomeBase * payoutPercent) / 100 / 14;
                } else if (isLunar) {
                  // ✅ ИСПРАВЛЕНО: Лунный - доход от ТЕКУЩЕГО visual_balance (eternal regression)
                  // Это совпадает с бэкендом: dailyProfit = visualBalance * (effectiveApr / 100)
                  dailyIncome = visualBalance * (currentApr / 100);
                } else {
                  // Звёздный: APR в день от actualAmount
                  dailyIncome = incomeBase * (currentApr / 100);
                }

                const incomeCurrency = isMarathon ? 'USDT' : 'SPERM';

                // Уменьшение баланса для лунного стейкинга (считаем от 200, не от 180)
                const burnedAmount = isLunar ? (initialVisualBalance - visualBalance) : 0;
                const burnProgress = isLunar ? ((burnedAmount / initialVisualBalance) * 100) : 0;
                const currentVisualBalance = visualBalance;

                // Для звездных депозитов - штрафы (по STAKING_ECONOMY.html)
                // depositAge уже объявлен выше
                let currentPenalty = 0;
                let daysUntilNoPenalty = 0;
                let nextPenaltyLevel = '';

                if (!isLunar && depositType !== 'marathon') {
                  if (depositAge < 30) {
                    currentPenalty = 20;
                    daysUntilNoPenalty = 30 - depositAge;
                    nextPenaltyLevel = '10%';
                  } else if (depositAge < 100) {
                    currentPenalty = 10;
                    daysUntilNoPenalty = 100 - depositAge;
                    nextPenaltyLevel = '5%';
                  } else if (depositAge < 200) {
                    currentPenalty = 5;
                    daysUntilNoPenalty = 200 - depositAge;
                    nextPenaltyLevel = '0%';
                  } else {
                    currentPenalty = 0;
                    daysUntilNoPenalty = 0;
                    nextPenaltyLevel = '';
                  }
                }

                // Для марафонных депозитов - периоды и прогресс
                let currentPeriod = 1;
                let totalPeriods = 20; // 20 периодов × 14 дней = 280 дней, ROI: 315%
                let daysInPeriod = 14;
                let currentDayInPeriod = 0;
                let daysUntilNextPayment = 0;
                let nextPaymentDate: Date | null = null;

                if (isMarathon) {
                  // Период берём из бэкенда (subscription_period)
                  currentPeriod = deposit.subscription_period || 1;
                  if (currentPeriod > totalPeriods) currentPeriod = totalPeriods;

                  // Дата следующей выплаты
                  if (deposit.next_subscription_date) {
                    nextPaymentDate = new Date(deposit.next_subscription_date);
                  } else {
                    const createdAt = new Date(deposit.created_at);
                    nextPaymentDate = new Date(createdAt.getTime() + (currentPeriod * daysInPeriod * 24 * 60 * 60 * 1000));
                  }

                  // День в периоде — по календарным дням (без учёта часов)
                  if (deposit.is_frozen) {
                    currentDayInPeriod = 0;
                    daysUntilNextPayment = daysInPeriod;
                  } else if (nextPaymentDate) {
                    const periodStartMs = nextPaymentDate.getTime() - daysInPeriod * 24 * 60 * 60 * 1000;
                    const periodStartDate = new Date(periodStartMs);
                    const startDay = new Date(periodStartDate.getFullYear(), periodStartDate.getMonth(), periodStartDate.getDate());
                    const today = new Date();
                    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    currentDayInPeriod = Math.min(daysInPeriod, Math.max(0, Math.floor((todayDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24))));
                    daysUntilNextPayment = daysInPeriod - currentDayInPeriod;
                  } else {
                    currentDayInPeriod = depositAge % daysInPeriod;
                    daysUntilNextPayment = daysInPeriod - currentDayInPeriod;
                  }
                }

                return (
                  <div
                    key={`deposit-${deposit.id || index}`}
                    className={`deposit-card-compact ${isExpanded ? 'expanded' : ''} ${isLunar ? 'lunar-deposit' : ''} ${isMarathon ? 'marathon-deposit' : ''}`}
                    data-type={depositType}
                  >
                    <button
                      type="button"
                      className="deposit-summary clickable"
                      onClick={() => setExpandedDeposit(isExpanded ? null : (deposit.id || index))}
                    >
                      <div className="deposit-left">
                        <span className="deposit-icon">
                          {depositType === 'lunar' ? '🌙' : depositType === 'marathon' ? '🎯' : '⭐'}
                        </span>
                        <div className="deposit-main-info">
                          {isLunar ? (
                            <>
                              <span className="deposit-amount-compact visual-x2">
                                {formatNumber(currentVisualBalance, 0)} SPERM
                                <span className="x2-badge">x2</span>
                              </span>
                              <span className="deposit-real-amount">
                                {t('staking.actuallyDeposited')}: {formatNumber(amount, 0)} SPERM
                              </span>
                            </>
                          ) : isMarathon ? (
                            <>
                              <span className="deposit-amount-compact marathon-amount">
                                {formatNumber(amount, 2)} USDT
                              </span>
                              <span className="deposit-marathon-progress">
                                {t('staking.period')} {currentPeriod}/{totalPeriods} · {t('staking.day')} {currentDayInPeriod}/{daysInPeriod}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="deposit-amount-compact">
                                {formatNumber(actualAmount, 0)} SPERM
                              </span>
                              <span className="deposit-age-compact">
                                {t('staking.age')}: {depositAge} {getDaysText(depositAge)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="deposit-right">
                        <span className="deposit-daily-income">
                          +{formatDailyIncome(dailyIncome)} {incomeCurrency}{t('staking.perDay')}
                        </span>
                        {isLunar ? (
                          <div className="deposit-burn-progress">
                            <div className="burn-bar-mini">
                              <div
                                className="burn-fill-mini"
                                style={{
                                  width: `${100 - burnProgress}%`,
                                  background: 'linear-gradient(90deg, #7b3ff2, #ff6348)'
                                }}
                              />
                            </div>
                            <span className="burn-text-mini">📉 {(100 - burnProgress).toFixed(0)}% {t('staking.remaining')}</span>
                          </div>
                        ) : isMarathon ? (
                          <div className="deposit-marathon-info">
                            <span className="marathon-payment-badge">💰 {t('staking.nextPayment')}</span>
                            <span className="marathon-payment-hint">
                              {nextPaymentDate ? (
                                nextPaymentDate.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                }) + ' ' + nextPaymentDate.toLocaleTimeString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              ) : (
                                t('staking.inDays', { days: daysUntilNextPayment, daysText: getDaysText(daysUntilNextPayment) })
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="deposit-penalty-info">
                            {/* Штраф не показываем в свернутом виде - только в развернутом */}
                            <span className="penalty-hint-compact">
                              {t('staking.age')}: {depositAge} {getDaysText(depositAge)}
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="expand-icon">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </button>
                    
                    {isExpanded && (
                      <div className="deposit-details">
                        <div className="detail-row">
                          <span>{t('staking.openDate')}</span>
                          <span>{new Date(deposit.created_at).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US')}</span>
                        </div>
                        {/* Убрано "до сгорания" - лунный стейкинг вечный */}
                        {!isLunar && !isMarathon && (
                          <div className="detail-row">
                            <span>{t('staking.depositAge')}</span>
                            <span>{depositAge} {getDaysText(depositAge)}</span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span>{t('staking.stakingType')}</span>
                          <span>
                            {depositType === 'lunar'
                              ? `🌙 ${t('staking.lunarTypeDesc', { apr: formatPercentValue(currentApr) })}`
                              : depositType === 'marathon'
                              ? `🎯 ${t('staking.marathonTypeDesc')}`
                              : `⭐ ${t('staking.starTypeDesc', { apr: formatPercentValue(currentApr) })}`
                            }
                          </span>
                        </div>

                        {/* Информация о штрафе для звездного стейкинга в развернутом виде */}
                        {!isLunar && !isMarathon && (
                          <div className="detail-row">
                            <span>{t('staking.withdrawalPenalty')}</span>
                            <span className={currentPenalty > 0 ? 'penalty-warning' : 'no-penalty-text'}>
                              {currentPenalty > 0
                                ? `${currentPenalty}% (${t('staking.inDays', { days: daysUntilNoPenalty, daysText: getDaysText(daysUntilNoPenalty) })} → ${nextPenaltyLevel})`
                                : `✅ ${t('staking.noPenalty')}`
                              }
                            </span>
                          </div>
                        )}

                        {/* 💎 Всего заработано с этого стейкинга */}
                        {!isMarathon && (
                          <div className="detail-row pending-profit-row">
                            <span>💰 {t('staking.totalEarned')}</span>
                            <span className="pending-profit-value">
                              {formatNumber(parseFloat(deposit.total_earned || 0), 2)} SPERM
                            </span>
                          </div>
                        )}

                        {/* Кнопка вывода для звездного стейкинга */}
                        {!isLunar && !isMarathon && (
                          <button
                            className="deposit-withdraw-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStarWithdraw(deposit);
                            }}
                          >
                            💰 {t('staking.withdrawFunds')}
                          </button>
                        )}

                        {/* 🎯 Блок для марафона - сумма вклада, прибыль и кнопки */}
                        {isMarathon && (
                          <>
                            <div className="detail-row">
                              <span>💵 {t('staking.marathonDepositAmount')}</span>
                              <span className="marathon-deposit-value">{formatNumber(amount, 2)} USDT</span>
                            </div>
                            <div className="detail-row">
                              <span>📊 {t('staking.marathonAccumulated')}</span>
                              <span>{formatNumber(actualAmount, 2)} USDT</span>
                            </div>
                            {/* Определяем: ожидает ли марафон довнесения (прибыль уже забрана ранее) */}
                            {(() => {
                              const waitingForContribution = deposit.is_frozen
                                && deposit.freeze_reason
                                && String(deposit.freeze_reason).startsWith('Ожидание довнесения');

                              return (<>
                            {/* Статус заморозки */}
                            {deposit.is_frozen && (
                              <div
                                className={`marathon-frozen-warning${waitingForContribution ? ' clickable' : ''}`}
                                onClick={() => {
                                  if (waitingForContribution && deposit.subscription_period < deposit.total_periods) {
                                    setSelectedMarathonForContribute(deposit);
                                    setMarathonContributeModalOpen(true);
                                  }
                                }}
                              >
                                {waitingForContribution
                                  ? `❄️ ${t('staking.marathonFrozenWarning')}`
                                  : (deposit.can_claim_profit || parseFloat(deposit.calculated_pending_profit || deposit.pending_profit || 0) > 0)
                                    ? `❄️ ${t('staking.marathonFrozenClaimFirst')}`
                                    : `❄️ ${t('staking.marathonFrozenWarning')}`
                                }
                              </div>
                            )}

                            {/* Информация о прибыли */}
                            {parseFloat(deposit.pending_profit || 0) > 0 ? (
                              <div className="detail-row marathon-profit-row">
                                <span>💰 {t('staking.marathonPeriodProfit')}</span>
                                <span className="marathon-profit-value">
                                  {formatNumber(parseFloat(deposit.pending_profit || 0), 2)} USDT
                                </span>
                              </div>
                            ) : (
                              <div className="detail-row">
                                <span>💰 {t('staking.marathonTotalEarned')}</span>
                                <span className="marathon-earned-value">
                                  {formatNumber(parseFloat(deposit.total_earned || 0), 2)} USDT ✓
                                </span>
                              </div>
                            )}

                            {/* Информация о следующем взносе */}
                            <div className="detail-row">
                              <span>📅 {t('staking.marathonNextDeposit')}</span>
                              <span>
                                {nextPaymentDate ? (
                                  nextPaymentDate.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }) + ' ' + t('staking.atTime') + ' ' + nextPaymentDate.toLocaleTimeString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                ) : t('staking.notDefined')}
                              </span>
                            </div>

                            {/* 💰 Накопленная прибыль ЭТОГО марафона (привязана к нему) */}
                            {parseFloat(deposit.accumulated_profit || 0) > 0 && (
                              <>
                                <div className="detail-row marathon-profit-row">
                                  <span>💰 {t('staking.marathonOwnProfit', 'Прибыль марафона')}</span>
                                  <span className="marathon-profit-value">
                                    {formatNumber(parseFloat(deposit.accumulated_profit || 0), 2)} USDT
                                  </span>
                                </div>
                                {deposit.is_promo && deposit.subscription_period < 2 ? (
                                  <div className="marathon-wait-hint">
                                    🔒 {t('staking.marathonPromoLocked', 'Вывод прибыли — со 2-го периода. Сейчас она идёт в зачёт продления.')}
                                  </div>
                                ) : (
                                  <button
                                    className="marathon-claim-btn"
                                    disabled={!!marathonBusy[deposit.id]}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleWithdrawMarathonProfit(deposit);
                                    }}
                                  >
                                    {marathonBusy[deposit.id]
                                      ? `⏳ ${t('staking.processing', 'Обработка...')}`
                                      : `💸 ${t('staking.withdrawMarathonProfit', 'Вывести прибыль')} (${formatNumber(parseFloat(deposit.accumulated_profit || 0), 2)} USDT)`}
                                  </button>
                                )}
                              </>
                            )}

                            {/* ЛОГИКА КНОПОК:
                                1. Если ожидает довнесения → показываем "Разморозить" (забрать НЕЛЬЗЯ)
                                2. Если can_claim_profit = true → показываем "Забрать прибыль"
                                3. Если прибыль забрана И заморожен → показываем "Разморозить"
                                4. Если период не завершён → показываем сколько осталось
                            */}

                            {/* Кнопка "Забрать прибыль" - НЕ показывается при ожидании довнесения */}
                            {!waitingForContribution && (deposit.can_claim_profit || parseFloat(deposit.pending_profit || 0) > 0) && (
                              <button
                                className="marathon-claim-btn"
                                disabled={!!marathonBusy[deposit.id]}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClaimMarathonProfit(deposit);
                                }}
                              >
                                {marathonBusy[deposit.id]
                                  ? `⏳ ${t('staking.processing', 'Обработка...')}`
                                  : `🎯 ${t('staking.claimProfit')} (${formatNumber(parseFloat(deposit.calculated_pending_profit || deposit.pending_profit || 0), 2)} USDT)`}
                              </button>
                            )}

                            {/* Кнопка "Разморозить" - показывается когда ожидает довнесения ИЛИ прибыль забрана */}
                            {(waitingForContribution || (!deposit.can_claim_profit && parseFloat(deposit.pending_profit || 0) === 0 && deposit.is_frozen)) && deposit.subscription_period < deposit.total_periods && (
                              <button
                                className="marathon-contribute-btn frozen"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMarathonForContribute(deposit);
                                  setMarathonContributeModalOpen(true);
                                }}
                              >
                                ❄️ {t('staking.marathonUnfreeze')}
                              </button>
                            )}

                            {/* Подсказка - когда период не завершён, показываем сколько дней осталось */}
                            {!waitingForContribution && !deposit.can_claim_profit && parseFloat(deposit.pending_profit || 0) === 0 && !deposit.is_frozen && deposit.subscription_period < deposit.total_periods && (
                              <div className="marathon-wait-hint">
                                ⏳ {deposit.days_until_claim > 0
                                  ? t('staking.marathonDaysLeft', { days: deposit.days_until_claim })
                                  : t('staking.marathonWaitNextPeriod')}
                              </div>
                            )}
                              </>);
                            })()}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Модальные окна загружаются лениво для ускорения */}
        <Suspense fallback={null}>
          {/* Модальное окно стейкинга */}
          {selectedStaking && (
            <StakingModal
              isOpen={modalOpen}
              onClose={() => {
                setModalOpen(false);
                setSelectedStaking(null);
              }}
              stakingType={selectedStaking}
              userBalance={userBalance}
              apr={selectedStaking === 'lunar' ? stakingSettings.lunar_apr : stakingSettings.star_apr}
              dailyGrowth={selectedStaking === 'lunar' ? stakingSettings.lunar_daily_growth : stakingSettings.star_daily_growth}
              claimInterval={selectedStaking === 'lunar' ? stakingSettings.lunar_claim_interval : stakingSettings.star_claim_interval}
              lockPeriod={selectedStaking === 'lunar' ? stakingSettings.lunar_lock_period : 0}
              withdrawalPenalty={selectedStaking === 'star' ? stakingSettings.star_withdrawal_penalty : 0}
              minAmount={selectedStaking === 'lunar' ? stakingSettings.lunar_min : stakingSettings.star_min}
              maxAmount={selectedStaking === 'lunar' ? stakingSettings.lunar_max : stakingSettings.star_max}
              spermPriceTon={(spermPrice || 0.40) / (tonPrice || 1.55)}
              tonAddress={tonAddress}
              onConfirm={handleConfirmStaking}
            />
          )}

          {/* Модальное окно успешного создания стейкинга */}
          {successModalOpen && (
            <StakingSuccessModal
              isOpen={successModalOpen}
              onClose={() => setSuccessModalOpen(false)}
              stakingAmount={successData.stakingAmount}
              claimedAmount={successData.claimedAmount}
              newBalance={successData.newBalance}
              stakingType={successData.stakingType}
            />
          )}

          {/* Модальное окно марафона */}
          {marathonModalOpen && (
            <MarathonModal
              isOpen={marathonModalOpen}
              onClose={() => setMarathonModalOpen(false)}
              userBalance={userBalance}
              usdtBalance={usdtBalance}
              spermPrice={spermPrice}
              tonAddress={tonAddress}
              onConfirm={handleConfirmMarathon}
            />
          )}

          {/* Модальное окно довнесения в марафон */}
          {marathonContributeModalOpen && selectedMarathonForContribute && (
            <MarathonContributeModal
              isOpen={marathonContributeModalOpen}
              onClose={() => {
                setMarathonContributeModalOpen(false);
                setSelectedMarathonForContribute(null);
              }}
              onSuccess={() => {
                fetchUserDeposits();
                fetchUserData();
              }}
              userBalance={userBalance}
              spermPrice={spermPrice}
              tonConnectUI={tonConnectUI}
              tonAddress={tonAddress}
              marathon={selectedMarathonForContribute}
            />
          )}

          {/* Модальное окно вывода из звездного стейкинга */}
          {starWithdrawModalOpen && (
            <StarWithdrawModal
              isOpen={starWithdrawModalOpen}
              onClose={() => {
                setStarWithdrawModalOpen(false);
                setSelectedDepositForWithdraw(null);
              }}
              deposit={selectedDepositForWithdraw}
              onConfirm={handleConfirmStarWithdraw}
            />
          )}

          {/* Модальное окно справки по тарифам */}
          {showInfoModal && (
            <StakingInfoModal
              stakingType={showInfoModal}
              onClose={() => setShowInfoModal(null)}
            />
          )}

          {/* 🎟️ Праздничная модалка активации промокода */}
          {promoSuccessOpen && (
            <PromoSuccessModal
              isOpen={promoSuccessOpen}
              amount={promoSuccessAmount}
              code={promoSuccessCode}
              onCelebrated={() => { api.post('/promo/notify-activation').catch(() => {}); }}
              onClose={() => setPromoSuccessOpen(false)}
            />
          )}

          {/* 🎯 Праздничная модалка создания марафона */}
          {marathonCreatedOpen && (
            <MarathonCreatedModal
              isOpen={marathonCreatedOpen}
              amountUsdt={marathonCreatedAmount}
              onClose={() => setMarathonCreatedOpen(false)}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default StakingPage;